import { useMemo, useState } from 'react'
import { useDirty } from '@/hooks/useDirty'
import type { ColumnDef } from '@tanstack/react-table'
import { ChevronDown, ChevronRight, Plus, Search } from 'lucide-react'
import type { CheckedState } from '@radix-ui/react-checkbox'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { Toolbar } from '@/components/Toolbar'
import { FormField } from '@/components/FormField'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  useCreateRole,
  useDeleteRole,
  useRolePermissions,
  useRolesFull,
  useSetRolePermissions,
  useUpdateRole,
} from '@/features/team/queries'
import type { RoleWithPerms } from '@/features/team/types'
import { groupPermissions } from '@/features/team/permissionGroups'

const errMsg = (err: unknown, fallback = 'Action failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function RolesTab() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const rolesQ = useRolesFull()
  const permsQ = useRolePermissions()
  const createRole = useCreateRole()
  const updateRole = useUpdateRole()
  const setPerms = useSetRolePermissions()
  const deleteRole = useDeleteRole()
  const canEdit = hasPermission('team.roles')

  const [editing, setEditing] = useState<RoleWithPerms | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [confirmDel, setConfirmDel] = useState<RoleWithPerms | null>(null)
  const [creating, setCreating] = useState(false)
  const [permSearch, setPermSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Hooks must run on every render in the same order, so derive memos from
  // the (possibly empty) data BEFORE any early return. permsQ.data may be
  // undefined while loading; we treat that as "no permissions yet" and the
  // grouped result is just an empty array.
  const allGrouped = useMemo(
    () => groupPermissions(permsQ.data?.permissions ?? []),
    [permsQ.data?.permissions],
  )

  // Dirty-check the role editor. For "edit" mode the baseline is the role
  // as it currently exists on the server (name + description + sorted perm
  // codes); for "create" mode the baseline is an empty role so any typed
  // character or checked permission flips the Save button on. Without this
  // the modal opened with Save already enabled even before the user did
  // anything.
  const formSnapshot = useMemo(
    () => ({
      name: name.trim(),
      description: description.trim(),
      permissions: Array.from(selected).sort(),
    }),
    [name, description, selected],
  )
  const baselineSnapshot = useMemo(() => {
    if (!editing) {
      return { name: '', description: '', permissions: [] as string[] }
    }
    return {
      name: editing.name,
      description: editing.description ?? '',
      permissions: [...editing.permissions].sort(),
    }
  }, [editing])
  const dirty = useDirty(formSnapshot, baselineSnapshot)

  // Apply the search filter inside each group so the section header still
  // appears (with its filtered counts) instead of vanishing entirely - lets
  // users see *which* feature group their query lives in.
  const filteredGroups = useMemo(() => {
    const q = permSearch.trim().toLowerCase()
    if (!q) return allGrouped
    return allGrouped
      .map((g) => ({
        ...g,
        permissions: g.permissions.filter(
          (p) =>
            p.code.toLowerCase().includes(q) ||
            (p.description ?? '').toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.permissions.length > 0)
  }, [allGrouped, permSearch])

  // Master "Expand all" checkbox state. Reflects the visible (post-filter)
  // groups: when search narrows the list, the box only counts those groups
  // because they're the only ones the user can interact with.
  const visibleGroupIds = useMemo(
    () => filteredGroups.map((g) => g.group.id),
    [filteredGroups],
  )
  const expandedVisibleCount = useMemo(
    () => visibleGroupIds.reduce((n, id) => (expanded.has(id) ? n + 1 : n), 0),
    [expanded, visibleGroupIds],
  )
  const allExpandedState: CheckedState =
    visibleGroupIds.length === 0
      ? false
      : expandedVisibleCount === visibleGroupIds.length
        ? true
        : expandedVisibleCount === 0
          ? false
          : 'indeterminate'

  // Master "Select all" checkbox state. Same scoping rule: counts the
  // permissions actually visible right now so a user filtering for
  // "invoice" can flip every invoice permission on without touching
  // anything else.
  const visiblePermCodes = useMemo(
    () => filteredGroups.flatMap((g) => g.permissions.map((p) => p.code)),
    [filteredGroups],
  )
  const visibleSelectedCount = useMemo(
    () => visiblePermCodes.reduce((n, c) => (selected.has(c) ? n + 1 : n), 0),
    [selected, visiblePermCodes],
  )
  const allSelectedState: CheckedState =
    visiblePermCodes.length === 0
      ? false
      : visibleSelectedCount === visiblePermCodes.length
        ? true
        : visibleSelectedCount === 0
          ? false
          : 'indeterminate'

  if (rolesQ.isLoading || permsQ.isLoading) {
    return (
      <Card>
        <CardContent className="py-10">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  const roles = rolesQ.data?.roles ?? []

  const openCreate = () => {
    setEditing(null)
    setName('')
    setDescription('')
    setSelected(new Set())
    setError(null)
    setPermSearch('')
    setExpanded(new Set())
    setCreating(true)
  }

  const openEdit = (r: RoleWithPerms) => {
    setEditing(r)
    setName(r.name)
    setDescription(r.description ?? '')
    setSelected(new Set(r.permissions))
    setError(null)
    setPermSearch('')
    setExpanded(new Set())
    setCreating(true)
  }

  const close = () => {
    setCreating(false)
    setEditing(null)
  }

  const togglePerm = (code: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })

  const toggleGroup = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const groupSelectionState = (codes: string[]): 'all' | 'some' | 'none' => {
    if (codes.length === 0) return 'none'
    let on = 0
    for (const c of codes) if (selected.has(c)) on++
    if (on === 0) return 'none'
    if (on === codes.length) return 'all'
    return 'some'
  }

  const setGroup = (codes: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev)
      for (const c of codes) {
        if (on) next.add(c)
        else next.delete(c)
      }
      return next
    })

  const toggleExpandAll = (next: CheckedState) => {
    // Click on `true` (or `'indeterminate'`, which Radix sends as boolean
    // when the user clicks an indeterminate box) expands every visible
    // group; `false` collapses them. We don't blow away expansion state
    // for groups outside the current filter - clearing the search restores
    // them.
    if (next === true) {
      setExpanded((prev) => {
        const out = new Set(prev)
        for (const id of visibleGroupIds) out.add(id)
        return out
      })
    } else {
      setExpanded((prev) => {
        const out = new Set(prev)
        for (const id of visibleGroupIds) out.delete(id)
        return out
      })
    }
  }

  const toggleSelectAll = (next: CheckedState) => {
    if (next === true) {
      setSelected((prev) => {
        const out = new Set(prev)
        for (const c of visiblePermCodes) out.add(c)
        return out
      })
    } else {
      setSelected((prev) => {
        const out = new Set(prev)
        for (const c of visiblePermCodes) out.delete(c)
        return out
      })
    }
  }


  const submit = async () => {
    setError(null)
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    try {
      if (editing) {
        // Two backend calls: update name/desc + set permissions
        if (editing.name !== name.trim() || (editing.description ?? '') !== description) {
          await updateRole.mutateAsync({
            id: editing.id,
            body: { name: name.trim(), description },
          })
        }
        await setPerms.mutateAsync({ id: editing.id, permissions: Array.from(selected) })
        toast({ title: 'Role updated.' })
      } else {
        await createRole.mutateAsync({
          name: name.trim(),
          description,
          permissions: Array.from(selected),
        })
        toast({ title: 'Role created.' })
      }
      close()
    } catch (err) {
      setError(errMsg(err))
    }
  }

  const onDelete = async (r: RoleWithPerms) => {
    try {
      await deleteRole.mutateAsync(r.id)
      toast({ title: 'Role deleted.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  const columns: ColumnDef<RoleWithPerms>[] = [
    {
      accessorKey: 'name',
      header: 'Role',
      cell: ({ row }) => (
        <span className="font-semibold">
          {row.original.name}
          {row.original.is_system ? (
            <Badge variant="secondary" className="ml-2">
              system
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => <span className="text-xs text-muted-foreground">{row.original.description ?? '-'}</span>,
    },
    { accessorKey: 'member_count', header: 'Members', cell: ({ row }) => row.original.member_count ?? 0 },
    {
      id: 'permCount',
      header: 'Permissions',
      cell: ({ row }) => row.original.permissions.length,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        canEdit ? (
          <div className="flex flex-wrap gap-1">
            <Button size="sm" variant="ghost" onClick={() => openEdit(row.original)}>
              Edit
            </Button>
            {!row.original.is_system ? (
              <Button size="sm" variant="danger" onClick={() => setConfirmDel(row.original)}>
                Delete
              </Button>
            ) : null}
          </div>
        ) : null,
    },
  ]

  return (
    <div className="space-y-3">
      <Toolbar className="justify-end">
        {canEdit ? (
          <Button onClick={openCreate}>
            <Plus className="size-4" />
            New role
          </Button>
        ) : null}
      </Toolbar>
      <Card>
        <CardContent className="p-0">
          <DataTable columns={columns} rows={roles} />
        </CardContent>
      </Card>

      <Modal
        open={creating}
        onOpenChange={(v) => !v && close()}
        title={editing ? `Edit role: ${editing.name}` : 'New role'}
        className="sm:max-w-3xl"
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!dirty || createRole.isPending || updateRole.isPending || setPerms.isPending}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField
            label="Name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
            disabled={editing?.is_system}
          />
          <FormField
            label="Description"
            control={
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
                rows={2}
              />
            }
          />
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="text-sm font-medium">Permissions</label>
              {/* Counts always reflect the post-filter scope so the
                  "X of Y" matches what the master checkboxes act on.
                  When no filter is active that's the whole catalog. */}
              <span className="text-xs text-muted-foreground">
                {visibleSelectedCount} of {visiblePermCodes.length} selected
                {permSearch.trim() ? (
                  <span className="ml-1 text-muted-foreground/70">
                    (filtered from {permsQ.data?.permissions?.length ?? 0})
                  </span>
                ) : null}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <div className="relative min-w-[220px] flex-1">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  placeholder="Search permissions by name or description…"
                  value={permSearch}
                  onChange={(e) => setPermSearch(e.currentTarget.value)}
                  className="pl-9"
                />
              </div>
              {/* Master toggles. Each is a single tri-state checkbox:
                  - unchecked → none, click checks all
                  - indeterminate → some, click checks the rest
                  - checked → all, click unchecks everything visible */}
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                <Checkbox
                  checked={allSelectedState}
                  onCheckedChange={toggleSelectAll}
                  disabled={visiblePermCodes.length === 0}
                  aria-label="Select all permissions"
                />
                Select all
              </label>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                <Checkbox
                  checked={allExpandedState}
                  onCheckedChange={toggleExpandAll}
                  disabled={visibleGroupIds.length === 0}
                  aria-label="Expand all permission groups"
                />
                Expand all
              </label>
            </div>
            <div className="mt-2 max-h-[50vh] space-y-1 overflow-y-auto rounded-md border p-2">
              {filteredGroups.length === 0 ? (
                <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                  No permissions match &ldquo;{permSearch}&rdquo;.
                </p>
              ) : (
                filteredGroups.map((g) => {
                  const codes = g.permissions.map((p) => p.code)
                  const state = groupSelectionState(codes)
                  // Auto-expand groups with active filter results so the user
                  // can see what matched without having to expand each one.
                  const isExpanded = expanded.has(g.group.id) || permSearch.trim().length > 0
                  return (
                    <div key={g.group.id} className="rounded-md border bg-muted/30">
                      <div className="flex items-center gap-2 px-2 py-1.5">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => toggleGroup(g.group.id)}
                          className="h-auto flex-1 justify-start gap-1.5 p-1 text-left font-normal hover:bg-transparent"
                          aria-expanded={isExpanded}
                          aria-controls={`group-${g.group.id}`}
                        >
                          {isExpanded ? (
                            <ChevronDown className="size-4 text-muted-foreground" aria-hidden />
                          ) : (
                            <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                          )}
                          <span className="text-sm font-medium">{g.group.label}</span>
                          <Badge variant="secondary" className="text-[10px]">
                            {g.permissions.filter((p) => selected.has(p.code)).length} / {g.permissions.length}
                          </Badge>
                        </Button>
                        <label
                          className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={state === 'all' ? true : state === 'some' ? 'indeterminate' : false}
                            onCheckedChange={() => setGroup(codes, state !== 'all')}
                            aria-label={`Select all ${g.group.label}`}
                          />
                          Select all
                        </label>
                      </div>
                      {g.group.description ? (
                        <p
                          className={cn(
                            'px-2 pb-1 text-[11px] text-muted-foreground',
                            isExpanded ? '' : 'hidden',
                          )}
                        >
                          {g.group.description}
                        </p>
                      ) : null}
                      <div
                        id={`group-${g.group.id}`}
                        className={cn(
                          'space-y-0.5 border-t bg-background px-2 py-1',
                          isExpanded ? '' : 'hidden',
                        )}
                      >
                        {g.permissions.map((p) => (
                          <label
                            key={p.code}
                            className="flex items-start gap-2 rounded-sm p-1 hover:bg-muted/50"
                          >
                            <Checkbox
                              checked={selected.has(p.code)}
                              onCheckedChange={() => togglePerm(p.code)}
                              className="mt-0.5"
                            />
                            <span className="text-sm leading-tight">
                              {p.description ? (
                                <span>{p.description}</span>
                              ) : (
                                <span className="text-muted-foreground">No description.</span>
                              )}
                              <code className="ml-1 text-[10px] text-muted-foreground">{p.code}</code>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      </Modal>

      <Modal.Confirm
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        title="Delete role"
        body={`Delete "${confirmDel?.name ?? ''}"? Members assigned to it will need a new role.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={async () => {
          const c = confirmDel
          setConfirmDel(null)
          if (c) await onDelete(c)
        }}
      />
    </div>
  )
}
